import React, { useState, useRef } from 'react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  Box,
  CircularProgress,
  FormLabel,
  Grid,
  Modal,
  styled,
  Typography,
} from '@mui/material'
import { BorderButtonPurple, CustomButton } from '../buttons'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_grids.scss'
import '../../styles/components/_inputs.scss'
import moment from 'moment-timezone'
import { faClose } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { truncate } from '../../config'
import { useTranslation } from 'react-i18next'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import { ROLE_CANDIDAT } from '../../data'
import { toast, ToastContainer, Slide } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import '../../styles/components/_toasts.scss'
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 50px)',
  height: 650,
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '10%',
  left: '50%',
  transform: 'translate(-50%, 0%)',
  padding: '30px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  rowGap: '20px',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 700,
    /*height: 500,*/
    height: 'auto',
    rowGap: '35px',
  },
}))

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 500,
  backgroundColor: lightTheme.palette.primary.b6,
  padding: '20px',

  [theme.breakpoints.up('sm')]: {
    /*height: 300,*/
    height: 'auto',
  },
}))

const DetailBesoinEntreprise = ({
  open,
  onClose,
  besoin,
  role,
  handleOpenUserCvsModal,
}) => {
  const { t } = useTranslation()
  const [seeMore, setSeeMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)

  const handleSeeMore = (event) => {
    setSeeMore(!seeMore)
  }

  const sendAnnonces = async () => {
    setLoading(true)

    try {
      const result = await Api.candidate.sendAnnonces(besoin.id)
      const handling = handlingErrors(result)

      if (handling.ok) {
        toast.success(t('box.successSendAnnonces'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const addRemainingContentAndSave = (
    doc,
    initialY,
    margin,
    t,
    besoin,
    setPdfLoading,
    fileName
  ) => {
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    let currentY

    // Robustly initialize currentY based on initialY
    if (
      typeof initialY === 'number' &&
      !isNaN(initialY) &&
      initialY >= margin &&
      initialY <
        pageHeight - margin - 20 /* min space for footer + small content */
    ) {
      currentY = initialY // Use initialY if it's a valid number and within plausible bounds
    } else {
      // initialY is invalid or out of comfortable starting range for content on the current page.
      // Log if initialY was problematic.
      if (typeof initialY !== 'number' || isNaN(initialY)) {
        console.warn(
          'PDF Generation: initialY from previous step was invalid. Resetting to top margin.',
          initialY
        )
      } else {
        // console.warn('PDF Generation: initialY was out of bounds for continued content. Starting fresh.', initialY);
      }

      // If not the very first page, or if initialY suggested we were far down a page, start a new one.
      if (
        doc.internal.getNumberOfPages() > 0 ||
        (typeof initialY === 'number' && initialY > margin + 20)
      ) {
        doc.addPage()
      }
      currentY = margin // Start at the top margin of the current or newly added page
    }

    console.log('[addRemainingContentAndSave] Initialized currentY:', currentY)

    // Ensure currentY starts within page bounds and has some minimal top margin (this is a secondary check after robust init)
    // This might be redundant now but kept for safety, ensuring currentY is not excessively large.
    if (currentY < margin || currentY >= pageHeight - margin - 30) {
      // 30 is a buffer for a small section + footer
      if (doc.internal.getNumberOfPages() > 0 && currentY > margin) {
        // This condition might need to be smarter to avoid adding a page if 'currentY = margin' just happened.
        // However, the robust initialization above should largely prevent needing this specific page add.
        // doc.addPage(); // Potentially re-evaluate if this specific addPage is still needed here.
      }
      currentY = margin // Fallback to margin if something went extremely wrong.
    }

    const addSection = (titleKey, items) => {
      if (!items || !items.length) return

      const titleText = t(titleKey)
      const itemText = items.join(', ')
      const contentMaxWidth = pageWidth - 2 * margin

      doc.setFontSize(11) // For item text measurement
      const itemLines = doc.splitTextToSize(itemText, contentMaxWidth)
      // Estimate height: (font size in pt / 72 * 25.4) * line spacing * number of lines
      // A simpler approximation for mm: (font size in pt * 0.3527) * 1.2 (line spacing) * num lines
      // Or, more practically, measure a single line height: doc.getTextDimensions('Aj').h
      const singleLineHeight = doc.getTextDimensions('Aj').h || 4 // approx 4mm for 11pt
      const itemContentHeight = itemLines.length * singleLineHeight

      doc.setFontSize(14) // For title measurement
      const titleHeight = doc.getTextDimensions(titleText).h || 5 // approx 5mm for 14pt

      const sectionMinHeight =
        titleHeight +
        2 /*line padding*/ +
        5 /*space*/ +
        itemContentHeight +
        5 /*bottom padding*/

      if (
        currentY + sectionMinHeight >
        pageHeight - margin - 20 /* footer space */
      ) {
        doc.addPage()
        currentY = margin
      }

      doc.setFontSize(14)
      doc.setTextColor(44, 62, 80)
      doc.text(titleText, margin, currentY)
      currentY += titleHeight // Move Y past title text

      doc.setDrawColor(238, 238, 238)
      doc.setLineWidth(0.5)
      doc.line(margin, currentY + 1, pageWidth - margin, currentY + 1)
      currentY += 1 + 5 // Move past line and add space

      if (
        currentY + itemContentHeight >
        pageHeight - margin - 20 /* footer space */
      ) {
        doc.addPage()
        currentY = margin
      }

      doc.setFontSize(11)
      doc.setTextColor(51, 51, 51)
      doc.text(itemLines, margin, currentY, { maxWidth: contentMaxWidth })
      currentY += itemContentHeight + 10 // Padding after section
    }

    addSection('filter.competences', besoin?.competences)
    addSection('filter.competencesAnnexes', besoin?.competencesAnnexes)
    addSection('filter.softskils', besoin?.softskils)
    addSection('filter.values', besoin?.valeurs)
    addSection('box.jobBenefits', besoin?.avantagesEntreprise)

    // Footer - ensure it's at the bottom of the current or a new page
    if (currentY > pageHeight - 25) {
      // If content is too close to where footer should start
      doc.addPage()
    }
    const finalPageHeight = doc.internal.pageSize.getHeight() // Potentially a new page
    doc.setDrawColor(238, 238, 238)
    doc.setLineWidth(0.5)
    doc.line(
      margin,
      finalPageHeight - 15,
      pageWidth - margin,
      finalPageHeight - 15
    )
    doc.setFontSize(10)
    doc.setTextColor(119, 119, 119)
    doc.text(
      `${t('pdf.generatedOn', 'Généré le')} ${moment().format(
        'DD/MM/YYYY [à] HH:mm'
      )} - JoberTech`,
      pageWidth - margin,
      finalPageHeight - 10,
      { align: 'right' }
    )

    doc.save(fileName)
    if (setPdfLoading) setPdfLoading(false)
  }

  const generatePDF = async () => {
    setPdfLoading(true)
    let tempDiv = null // For cleanup in finally block

    try {
      const fileName = `detail-poste-${
        besoin?.poste?.titre
          ? besoin.poste.titre.replace(/[^a-z0-9]/gi, '-').toLowerCase()
          : 'sans-titre'
      }.pdf`

      // Create a single PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      // Constants for all pages
      const margin = 10
      const pageWidth = pdf.internal.pageSize.getWidth()

      // ===== PAGE 1: Job Description =====
      let y = margin

      // Title for the first page
      pdf.setFont('helvetica', '')
      pdf.setFontSize(18)
      pdf.setTextColor(44, 62, 80)
      pdf.text(
        t('box.title.detailJob') + ' - ' + besoin?.poste?.titre,
        pageWidth / 2,
        y,
        { align: 'center' }
      )
      y += 10
      pdf.setDrawColor(238, 238, 238)
      pdf.setLineWidth(0.5)
      pdf.line(margin, y, pageWidth - margin, y)
      y += 5

      // Description title
      pdf.setFontSize(14)
      pdf.setTextColor(44, 62, 80)
      pdf.text(t('box.jobDescription'), pageWidth / 2, y, { align: 'center' })
      y += 2
      pdf.setDrawColor(238, 238, 238)
      pdf.setLineWidth(0.5)
      pdf.line(margin, y, pageWidth - margin, y)
      y += 7

      if (
        besoin?.descriptifPoste &&
        typeof besoin.descriptifPoste === 'string' &&
        besoin.descriptifPoste.trim() !== ''
      ) {
        // Prepare HTML content for the first page
        tempDiv = document.createElement('div')
        tempDiv.style.height = 'auto'
        tempDiv.style.width = '80mm'
        tempDiv.style.fontFamily = 'Arial, Helvetica, sans-serif'
        tempDiv.style.fontSize = '6px'
        tempDiv.style.lineHeight = '1.2'
        tempDiv.style.color = '#333'
        tempDiv.innerHTML = besoin.descriptifPoste

        document.body.appendChild(tempDiv)

        // Render HTML content on the first page
        pdf.html(tempDiv, {
          x: margin,
          y: y,
          html2canvas: {
            scale: 0.6,
            useCORS: true,
            logging: true,
          },
          autoPaging: 'text',
          width: pageWidth - 2 * margin,
          windowWidth: tempDiv.offsetWidth,
          callback: function (docInstance) {
            // ===== PAGE 2: Basic Job Information =====
            docInstance.addPage()
            let y = margin

            // Add basic info title
            docInstance.setFontSize(14)
            docInstance.setTextColor(44, 62, 80)
            docInstance.text(
              t('box.basicInfo') || 'Informations de base',
              pageWidth / 2,
              y,
              { align: 'center' }
            )
            y += 2
            docInstance.setDrawColor(238, 238, 238)
            docInstance.setLineWidth(0.5)
            docInstance.line(margin, y, pageWidth - margin, y)
            y += 5

            // Basic Info Table
            docInstance.setFontSize(11)
            docInstance.setTextColor(51, 51, 51)
            autoTable(docInstance, {
              startY: y,
              margin: { left: margin, right: margin },
              styles: { fontSize: 10, cellPadding: 1.5, overflow: 'linebreak' },
              body: [
                [t('box.jobTitle'), besoin?.poste?.titre || 'N/A'],
                [t('box.jobNumber'), besoin?.nbrPoste || 'N/A'],
                [t('box.jobContractType'), besoin?.typeContrat || 'N/A'],
                [t('box.jobPositionType'), besoin?.type || 'N/A'],
                [
                  t('box.jobExpectedStartDate'),
                  besoin?.dateDebut
                    ? moment(besoin.dateDebut)
                        .tz('Europe/Paris')
                        .format('DD/MM/YYYY')
                    : 'N/A',
                ],
                [t('box.jobPositionHours'), besoin?.horaires || 'N/A'],
                [
                  t('box.jobSalary'),
                  `${besoin?.salaire || 'N/A'} ${besoin?.devise || ''} ${
                    besoin?.salaireSur ? `/${t('box.mounth')}` : ''
                  }`,
                ],
                [
                  t('box.jobLocation'),
                  [besoin?.lieu, besoin?.pays].filter(Boolean).join(' / ') ||
                    'N/A',
                ],
                [
                  t('filter.languages'),
                  besoin?.besoinLangues
                    ?.map((item) => item.langue)
                    .join(', ') || 'N/A',
                ],
                [
                  t('box.jobTelework'),
                  besoin?.teletravailPropose !== undefined
                    ? `${besoin.teletravailPropose} ${t('box.days')}`
                    : 'N/A',
                ],
                [
                  t('box.timeWorkType'),
                  besoin?.typeTempsTravail
                    ? besoin.typeTempsTravail === 'partiel'
                      ? `${t('box.timeWorkType2')} (${
                          besoin?.tempPartiel || '0'
                        }%)`
                      : t('box.timeWorkType1')
                    : 'N/A',
                ],
                [t('box.jobReport'), besoin?.reportDirect || 'N/A'],
                [
                  t('box.jobTrip'),
                  besoin?.deplacement ? t('button.yes') : t('button.no'),
                ],
                [
                  t('box.jobPermit'),
                  besoin?.permisRequis ? t('button.yes') : t('button.no'),
                ],
                [
                  t('box.jobVehicle'),
                  besoin?.vehiculeRequis ? t('button.yes') : t('button.no'),
                ],
              ],
              theme: 'plain',
              tableLineColor: [238, 238, 238],
              tableLineWidth: 0.1,
              columnStyles: {
                0: {
                  fontStyle: 'bold',
                  textColor: [44, 62, 80],
                  cellWidth: 60,
                },
                1: { textColor: [51, 51, 51] },
              },
            })

            // ===== PAGE 3: Competences and Other Sections =====
            docInstance.addPage()

            // Add remaining content on the third page
            addRemainingContentAndSave(
              docInstance,
              margin,
              margin,
              t,
              besoin,
              setPdfLoading,
              fileName
            )
          },
        })

        // Return here because pdf.html is asynchronous
        return
      } else {
        // No description content
        pdf.setFontSize(11)
        pdf.setTextColor(51, 51, 51)
        pdf.text('N/A', margin, y, { maxWidth: pageWidth - 2 * margin })
        y += 10

        // Add a new page for basic info
        pdf.addPage()
        y = margin

        // Add basic info title
        pdf.setFontSize(14)
        pdf.setTextColor(44, 62, 80)
        pdf.text(
          t('box.basicInfo') || 'Informations de base',
          pageWidth / 2,
          y,
          { align: 'center' }
        )
        y += 10
        pdf.setDrawColor(238, 238, 238)
        pdf.setLineWidth(0.5)
        pdf.line(margin, y, pageWidth - margin, y)
        y += 4

        // Basic Info Table
        pdf.setFontSize(11)
        pdf.setTextColor(51, 51, 51)
        autoTable(pdf, {
          startY: y,
          margin: { left: margin, right: margin },
          styles: { fontSize: 10, cellPadding: 1.5, overflow: 'linebreak' },
          body: [
            [t('box.jobTitle'), besoin?.poste?.titre || 'N/A'],
            [t('box.jobNumber'), besoin?.nbrPoste || 'N/A'],
            [t('box.jobContractType'), besoin?.typeContrat || 'N/A'],
            [t('box.jobPositionType'), besoin?.type || 'N/A'],
            [
              t('box.jobExpectedStartDate'),
              besoin?.dateDebut
                ? moment(besoin.dateDebut)
                    .tz('Europe/Paris')
                    .format('DD/MM/YYYY')
                : 'N/A',
            ],
            [t('box.jobPositionHours'), besoin?.horaires || 'N/A'],
            [
              t('box.jobSalary'),
              `${besoin?.salaire || 'N/A'} ${besoin?.devise || ''} ${
                besoin?.salaireSur ? `/${t('box.mounth')}` : ''
              }`,
            ],
            [
              t('box.jobLocation'),
              [besoin?.lieu, besoin?.pays].filter(Boolean).join(' / ') || 'N/A',
            ],
            [
              t('filter.languages'),
              besoin?.besoinLangues?.map((item) => item.langue).join(', ') ||
                'N/A',
            ],
            [
              t('box.jobTelework'),
              besoin?.teletravailPropose !== undefined
                ? `${besoin.teletravailPropose} ${t('box.days')}`
                : 'N/A',
            ],
            [
              t('box.timeWorkType'),
              besoin?.typeTempsTravail
                ? besoin.typeTempsTravail === 'partiel'
                  ? `${t('box.timeWorkType2')} (${besoin?.tempPartiel || '0'}%)`
                  : t('box.timeWorkType1')
                : 'N/A',
            ],
            [t('box.jobReport'), besoin?.reportDirect || 'N/A'],
            [
              t('box.jobTrip'),
              besoin?.deplacement ? t('button.yes') : t('button.no'),
            ],
            [
              t('box.jobPermit'),
              besoin?.permisRequis ? t('button.yes') : t('button.no'),
            ],
            [
              t('box.jobVehicle'),
              besoin?.vehiculeRequis ? t('button.yes') : t('button.no'),
            ],
          ],
          theme: 'plain',
          tableLineColor: [238, 238, 238],
          tableLineWidth: 0.1,
          columnStyles: {
            0: { fontStyle: 'bold', textColor: [44, 62, 80], cellWidth: 60 },
            1: { textColor: [51, 51, 51] },
          },
        })

        // Add a new page for competences
        pdf.addPage()

        // Add remaining content on the third page
        addRemainingContentAndSave(
          pdf,
          margin,
          margin,
          t,
          besoin,
          setPdfLoading,
          fileName
        )
      }
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error)
      alert(
        'Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.'
      )
      setPdfLoading(false)
    } finally {
      // Clean up any created elements
      if (tempDiv && tempDiv.parentNode) {
        tempDiv.parentNode.removeChild(tempDiv)
      }
    }
  }

  return (
    <>
      <Modal
        open={open}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(51, 51, 51, 0.65)',
          },
          overflow: 'auto',
        }}
      >
        <ModalContainer>
          <Typography variant={'subtitle2'} textTransform={'uppercase'}>
            {t('box.title.detailJob')}
            <Box
              sx={{
                position: 'absolute',
                right: '10px',
                top: '10px',
                cursor: 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faClose} fontSize={25} onClick={onClose} />
            </Box>
          </Typography>
          <BoxContainer>
            <Grid
              container
              spacing={2}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobTitle')} : <b>{besoin?.poste?.titre}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobNumber')} : <b>{besoin?.nbrPoste}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobContractType')} : <b>{besoin.typeContrat}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobPositionType')} : <b>{besoin.type}</b>
                </FormLabel>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobExpectedStartDate')} :{' '}
                  <b>
                    {moment(besoin.dateDebut)
                      .tz('Europe/Paris')
                      .format('DD/MM/YYYY')}
                  </b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobPositionHours')} : <b>{besoin?.horaires}</b>
                </FormLabel>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobSalary')} :{' '}
                  <b>
                    {besoin?.salaire} {besoin.devise} / {besoin.salaireSur}{' '}
                    {t('box.mounth')}
                  </b>
                </FormLabel>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobLocation')} :{' '}
                  <b>
                    {besoin.lieu} / {besoin?.pays}
                  </b>
                </FormLabel>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('filter.languages')} :{' '}
                  <b>
                    {besoin.besoinLangues
                      .map((item) => `${item.langue}`)
                      .join(', ')}
                  </b>
                </FormLabel>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                sx={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }}
              >
                <FormLabel>{t('box.jobDescription')} :</FormLabel>
                <b>
                  {seeMore ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: besoin?.descriptifPoste.trim(),
                      }}
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: truncate(besoin?.descriptifPoste.trim()),
                      }}
                    />
                  )}
                </b>
                {besoin?.descriptifPoste.length > 150 && (
                  <a
                    href={
                      seeMore
                        ? '#' + t('button.seeLess')
                        : '#' + t('button.seeMore')
                    }
                    style={{
                      cursor: 'pointer',
                      color: '#96A0FF',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                    }}
                    onClick={handleSeeMore}
                  >
                    {seeMore ? t('button.seeLess') : t('button.seeMore')}
                  </a>
                )}
              </Grid>
              {/* <Grid
              item
              xs={12}
              sm={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '5px',
              }}
            >
              <FormLabel>
                {t('box.jobBenefits')} : <b>{besoin?.avantages}</b>
              </FormLabel>
            </Grid> */}

              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobTelework')}{' '}
                  <b>
                    {besoin?.teletravailPropose}{' '}
                    {/* {besoin?.teletravailPropose > 1 ? 'jours' : 'jour'} */}
                  </b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.timeWorkType')} :{' '}
                  <b>
                    {besoin?.typeTempsTravail === 'partiel'
                      ? t('box.timeWorkType2') +
                        ' (' +
                        besoin?.tempPartiel +
                        '%)'
                      : t('box.timeWorkType1')}
                  </b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobReport')} : <b>{besoin?.reportDirect}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('filter.competences')} :{' '}
                  <b>{besoin.competences.join(', ')}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('filter.competencesAnnexes')} :{' '}
                  <b>{besoin.competencesAnnexes.join(', ')}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('filter.softskils')} : <b>{besoin.softskils.join(', ')}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('filter.values')} : <b>{besoin.valeurs.join(', ')}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobBenefits')} :{' '}
                  <b>{besoin.avantagesEntreprise.join(', ')}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobTrip')} :{' '}
                  <b>{besoin.deplacement ? t('button.yes') : t('button.no')}</b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobPermit')} :{' '}
                  <b>
                    {besoin.permisRequis ? t('button.yes') : t('button.no')}
                  </b>
                </FormLabel>
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <FormLabel>
                  {t('box.jobVehicle')} :{' '}
                  <b>
                    {besoin.vehiculeRequis ? t('button.yes') : t('button.no')}
                  </b>
                </FormLabel>
              </Grid>
            </Grid>
          </BoxContainer>
          {/*<Box className={"wrapperButtons"} width={"100%"}>*/}
          <Box className={'blockButtons'}>
            {role === ROLE_CANDIDAT && (
              <CustomButton
                displayIcon={'none'}
                handleClick={() => {
                  besoin.postuler &&
                  moment().isBefore(moment(besoin.dateExpiration))
                    ? handleOpenUserCvsModal(besoin.id)
                    : sendAnnonces()
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : besoin.postuler &&
                  moment().isBefore(moment(besoin.dateExpiration)) ? (
                  t('button.postuler')
                ) : (
                  t('button.interestedPosition')
                )}
              </CustomButton>
            )}
            {role !== ROLE_CANDIDAT && (
              <>
                <CustomButton
                  displayIcon={'none'}
                  handleClick={generatePDF}
                  style={{ marginRight: '10px', backgroundColor: '#dc3545' }}
                >
                  {pdfLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Télécharger en PDF'
                  )}
                </CustomButton>
              </>
            )}
            <BorderButtonPurple displayIcon={'none'} handleClick={onClose}>
              {t('button.close')}
            </BorderButtonPurple>
          </Box>
          {/*</Box>*/}
        </ModalContainer>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        transition={Slide}
        className={'toast'}
      />
    </>
  )
}

export default DetailBesoinEntreprise
